import React from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { HugeiconsIcon } from '@hugeicons/react'
import Shield01Icon from '@hugeicons/core-free-icons/Shield01Icon'
import CheckmarkCircle01Icon from '@hugeicons/core-free-icons/CheckmarkCircle01Icon'
import ArrowRight02Icon from '@hugeicons/core-free-icons/ArrowRight02Icon'
import MinusSignIcon from '@hugeicons/core-free-icons/MinusSignIcon'
import PlusSignIcon from '@hugeicons/core-free-icons/PlusSignIcon'
import Image from '@/components/shared/image'
import CTA from '@/components/LandingPage/CTA'
import { AskAIExplain } from '@/components/ask-ai-to-explain'
import { Button } from '@/components/ui/button'
import {
  createLLMOMeta,
  createFAQSchema,
  createWebPageSchema,
  createBreadcrumbSchema,
  createOrganizationSchema,
  createSoftwareApplicationSchema,
} from '@/lib/seo'
import { cn } from '@/lib/utils'
import { useColorTheme } from '@/components/providers/color-theme-provider'
import { useAuth } from '@/components/auth/AuthProvider'

// FAQ data for structured data and UI
const faqData = [
  {
    icon: "🛡️",
    question: 'Is Zapify safe for Instagram accounts?',
    answer:
      'Zapify is designed around safer access: Meta authorization, no Instagram password sharing, and automation for eligible Business and Creator accounts. Safety also depends on how you use automation, so avoid spam, misleading messages, and aggressive sending.',
  },
  {
    icon: "🔒",
    question: 'Does Zapify need my Instagram password?',
    answer:
      "No. Zapify should connect through Meta's permission flow. If any page claiming to be Zapify asks for your Instagram password directly, stop and contact Zapify support.",
  },
  {
    icon: "✅",
    question: 'Is Zapify a Meta Verified blue-check provider?',
    answer:
      "No. Zapify does not sell Meta Verified subscriptions or guarantee a blue badge. This page is about Zapify's verified technology/app approval proof for Instagram automation workflows.",
  },
  {
    icon: "⚠️",
    question: 'Can automation get my account banned?',
    answer:
      "No platform can honestly promise zero risk. Zapify reduces avoidable risk by avoiding password-based access and risky browser automation. You are still responsible for following Meta's rules, sending relevant messages, and respecting user consent.",
  },
  {
    icon: "⚙️",
    question: 'What can I automate with Zapify?',
    answer:
      'Zapify helps automate Instagram DM workflows such as comment-to-DM replies, story reply follow-ups, link delivery, offer replies, and lead capture for creators, brands, ecommerce stores, and agencies.',
  },
  {
    icon: "🔌",
    question: 'Can I disconnect Zapify later?',
    answer:
      'Yes. Because the connection is designed around Meta authorization, account owners should be able to review and revoke connected app access through their Meta account settings.',
  },
  {
    icon: "📄",
    question: 'Where can I read Zapify\'s privacy and legal terms?',
    answer:
      'Review the current legal pages before connecting: the Zapify Privacy Policy, Terms of Service, and Contact page.',
  },
] as const

// FAQ slides for the left panel — light + dark pairs (same pattern as landing page FAQ)
const verifiedFaqSlides = [
  { light: '/temp/faq1.webp', dark: '/temp/faq1_dark.webp' },
  { light: '/temp/faq2.webp', dark: '/temp/faq2_dark.webp' },
] as const

// Trust highlights data
const trustHighlights = [
  {
    title: 'No password sharing',
    description:
      'Connect supported accounts through Meta instead of sending your Instagram password to a tool.',
  },
  {
    title: 'Official API approach',
    description:
      'Built for eligible Instagram Business and Creator accounts using Meta-supported access.',
  },
  {
    title: 'Proof-first page',
    description:
      'Review app approval, Microsoft for Startups, and Stripe Startup Program screenshots below.',
  },
] as const

// Safety features data
const safetyFeatures = [
  {
    title: 'Connect through Meta',
    description:
      "Zapify is written for Meta's secure authorization flow. You review permissions directly with Meta and can disconnect access from your account settings.",
  },
  {
    title: 'No Instagram password required',
    description:
      'A safe automation tool should not need your Instagram password. Zapify focuses on authorized account connection instead of password sharing.',
  },
  {
    title: 'Responsible automation',
    description:
      'Zapify helps automate allowed workflows such as comment-to-DM replies and lead capture. Your message content should stay relevant, requested, and non-spammy.',
  },
] as const

// Proof screenshots data
const proofScreenshots = [
  {
    src: '/new-verified-meta-ss.webp',
    title: 'Meta app approval',
    description:
      "Shows Zapify's Meta app approval proof for the Instagram automation connection.",
  },
  {
    src: 'https://cms.zapify.pro/uploads/microsoft_7f378dec9d.png',
    title: 'Microsoft for Startups',
    description:
      'Startup program proof shown by Zapify for credibility and company trust.',
  },
  {
    src: 'https://cms.zapify.pro/uploads/stripe_201cb7353d.png',
    title: 'Stripe Startup Program',
    description:
      'Stripe Startup Program proof shown by Zapify for additional trust context.',
  },
] as const

// Risk comparison data
const riskComparison = [
  {
    question: 'How do I connect Instagram?',
    zapify: 'Meta authorization\nReview permissions through Meta for eligible accounts.',
    risky: 'Password or session access\nThe tool asks for your password, cookies, or manual browser login.',
  },
  {
    question: 'How are messages automated?',
    zapify: 'Official API-led workflows\nBuilt around supported automation such as comment-to-DM and replies.',
    risky: 'Scraping or hidden bots\nThe tool imitates a human in a browser or bypasses platform controls.',
  },
  {
    question: 'Are limits respected?',
    zapify: 'Policy-aware setup\nAutomation should respect Meta\'s permissions, platform rules, and sending constraints.',
    risky: 'Unlimited promises\nThe tool advertises unlimited DMs, mass outreach, or engagement shortcuts.',
  },
  {
    question: 'Can I verify the company?',
    zapify: 'Visible proof and legal pages\nReview screenshots, terms, privacy policy, and contact options before connecting.',
    risky: 'Anonymous or vague\nNo proof, unclear owner, no support route, or missing privacy information.',
  },
] as const

// Safe setup steps
const safeSetupSteps = [
  {
    title: 'Confirm you are on the official Zapify site',
    description:
      'Use zapify.pro and avoid links from unknown senders that ask for login details.',
  },
  {
    title: 'Connect an eligible Business or Creator account through Meta',
    description:
      "Review Meta's permission screen carefully and approve only the account you intend to automate.",
  },
  {
    title: 'Build consent-based automations',
    description:
      'Use clear triggers such as comment keywords, story replies, or requested links instead of cold spam.',
  },
  {
    title: 'Test before sending at scale',
    description:
      'Run your flow on a test post or small audience first, then monitor replies, complaints, and opt-outs.',
  },
  {
    title: 'Pause or update flows anytime',
    description:
      'Keep messages accurate, helpful, and aligned with your current offer, inventory, or campaign.',
  },
] as const

// Trust checklist
const goodSigns = [
  'The tool connects through Meta authorization.',
  'The permission screen is clear and visible.',
  'The company has a privacy policy, terms, and contact route.',
  'The tool explains what automation can and cannot do.',
] as const

const warningSigns = [
  'It asks for your Instagram password.',
  'It promises unlimited DMs or guaranteed account growth.',
  'It hides how it connects to Instagram.',
  'It encourages scraping, spam, or policy bypasses.',
] as const

export const Route = createFileRoute('/verified')({
  head: () => ({
    title: 'Zapify Meta Verified Instagram DM Automation | Safe Official API',
    meta: [
      ...createLLMOMeta({
        title: 'Zapify Meta Verified Instagram DM Automation | Safe Official API',
        description:
          'Zapify is a Meta verified Instagram DM automation platform for creators, brands, and agencies. Connect through Meta, keep your password private, and automate safely.',
        url: 'https://zapify.pro/verified',
      }),
      {
        name: 'keywords',
        content:
          'Zapify Meta Verified, Meta verified Instagram DM automation, Meta approved Instagram automation, Instagram DM automation official API, safe Instagram automation, Instagram comment to DM automation, Instagram direct message automation, Instagram Business Creator accounts, no password Instagram automation, Instagram lead capture automation',
      },
      { name: 'robots', content: 'index, follow, max-image-preview:large' },
      { name: 'author', content: 'Zapify' },
      { name: 'theme-color', content: '#eeb5d1' },
    ],
    links: [
      { rel: 'canonical', href: 'https://zapify.pro/verified' },
      { rel: 'preload', as: 'image', href: '/logo.webp' },
      { rel: 'preload', as: 'image', href: '/new-verified-meta-ss.webp' },
      {
        rel: 'preload',
        as: 'image',
        href: 'https://cms.zapify.pro/uploads/microsoft_7f378dec9d.png',
      },
      {
        rel: 'preload',
        as: 'image',
        href: 'https://cms.zapify.pro/uploads/stripe_201cb7353d.png',
      },
    ],
    scripts: [
      createOrganizationSchema(),
      createSoftwareApplicationSchema(),
      createFAQSchema([...faqData]),
      createWebPageSchema({
        name: 'Zapify Meta Verified Instagram DM Automation',
        description:
          'Zapify is a Meta verified Instagram DM automation platform for creators, brands, and agencies.',
        url: 'https://zapify.pro/verified',
      }),
      createBreadcrumbSchema({
        itemListElement: [
          { position: 1, name: 'Home', item: 'https://zapify.pro' },
          { position: 2, name: 'Meta Verified', item: 'https://zapify.pro/verified' },
        ],
      }),
    ],
  }),
  component: VerifiedPage,
})

export function VerifiedPage() {
  const { themeColor } = useColorTheme()
  const { openAuth } = useAuth()
  const [modalImage, setModalImage] = React.useState<string | null>(null)
  const [faqSlideIndex, setFaqSlideIndex] = React.useState(0)
  const [faqOpenIndex, setFaqOpenIndex] = React.useState(0)

  React.useEffect(() => {
    const id = window.setInterval(() => {
      setFaqSlideIndex((prev) => (prev + 1) % verifiedFaqSlides.length)
    }, 2800)
    return () => window.clearInterval(id)
  }, [])

  return (
    <main className="min-h-screen w-full bg-white dark:bg-black transition-colors">
      {/* Hero Section */}
      <section className="relative w-full overflow-hidden mt-20">
        {/* Background gradients */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 opacity-[0.4] dark:opacity-[0.12]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(135deg, rgba(160,106,249,0.05) 0px, rgba(160,106,249,0.05) 2px, transparent 2px, transparent 24px)',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[min(55vh,480px)] bg-[radial-gradient(ellipse_65%_40%_at_50%_0%,rgba(160,106,249,0.08),transparent)] dark:bg-[radial-gradient(ellipse_65%_40%_at_50%_0%,rgba(160,106,249,0.06),transparent)]"
        />

        <div className="mx-auto w-full max-w-7xl px-4 pt-24 pb-12 sm:px-6 sm:pt-28 sm:pb-16 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[1.08fr_0.92fr] lg:gap-12">
            {/* Left: Content */}
            <div className="flex flex-col">
              {/* Eyebrow */}
              <div className="mb-5 inline-flex items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400">
                  <span className="h-2 w-2 rounded-full bg-green-500" />
                  Meta verified technology page for Zapify
                </span>
              </div>

              <h1 className="text-balance text-[clamp(2rem,5vw,3.5rem)] font-extrabold leading-[1.1] tracking-tight text-foreground">
                Zapify Meta Verified Instagram DM Automation
              </h1>

              <p className="mt-5 max-w-2xl text-[1.08rem] leading-relaxed text-muted-foreground">
                Zapify helps creators, brands, and agencies automate Instagram
                comments, direct messages, story replies, and lead capture
                through a safer Meta authorization flow. Your Instagram password
                stays private, and your account remains in your control.
              </p>

              <div className="mt-7 flex flex-wrap gap-3">
                <Button
                  size="lg"
                  onClick={() => openAuth()}
                  className={cn(
                    'h-12 rounded-full px-8 text-sm font-bold shadow-md shadow-primary/20 sm:text-base',
                    themeColor === 'yellow' && '!text-black'
                  )}
                >
                  Start free
                  <HugeiconsIcon
                    icon={ArrowRight02Icon}
                    size={17}
                    color="currentColor"
                    className="ml-0.5"
                  />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="h-12 rounded-full px-6 text-sm font-semibold sm:text-base"
                >
                  <Link to="/contact">Ask a safety question</Link>
                </Button>
              </div>

              {/* Ask AI to Explain - inline card */}
              <div className="mt-6">
                <AskAIExplain
                  context="security"
                  meta={{ pageTitle: "Zapify Meta Verified Tech Provider", pageUrl: "https://zapify.pro/verified" }}
                  isCard
                />
              </div>
            </div>

            {/* Right: Proof Card with Trust Cards Row */}
            <aside className="relative flex flex-col gap-4">
              <div className="rounded-2xl border border-border/70 bg-card p-4 shadow-lg dark:bg-card/80">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <span className="text-[0.7rem] font-bold uppercase tracking-wider text-primary">
                      Verification proof
                    </span>
                    <h2 className="mt-0.5 text-base font-bold text-foreground">
                      Meta app approval screenshot
                    </h2>
                  </div>
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[0.7rem] font-bold text-green-700 dark:bg-green-900 dark:text-green-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                    Visible proof
                  </span>
                </div>
                <div 
                  className="overflow-hidden rounded-lg border border-border/60 bg-muted/30 cursor-pointer hover:opacity-90 transition-opacity relative group" 
                  onClick={() => setModalImage('/new-verified-meta-ss.webp')}
                >
                  <Image
                    src="/new-verified-meta-ss.webp"
                    alt="Zapify Meta app approval screenshot shown as verification proof - click to view full size"
                    width={1200}
                    height={800}
                    className="w-full object-contain"
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/5 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-background/90 backdrop-blur-sm rounded-full px-3 py-1.5 text-xs font-medium shadow-lg">
                      Click to enlarge
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                  Click image to view full size. Check the screenshot, then review the exact permissions shown
                  by Meta when you connect. Never approve permissions you do
                  not understand.
                </p>
              </div>

              {/* Trust Cards in 1 Row */}
              <div className="grid grid-cols-3 gap-2">
                {trustHighlights.map((item) => (
                  <div
                    key={item.title}
                    className="flex flex-col items-center text-center rounded-xl border border-border/60 bg-card/50 p-3 dark:bg-card/30"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 mb-2">
                      <HugeiconsIcon
                        icon={Shield01Icon}
                        size={18}
                        className="text-primary"
                      />
                    </div>
                    <h3 className="text-xs font-bold text-foreground leading-tight">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-[10px] leading-tight text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Proof Section - Moved after Hero */}
      <section className="w-full border-y border-border/40 bg-muted/30 py-12 dark:bg-muted/10 sm:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 max-w-2xl">
            <span className="text-[0.7rem] font-bold uppercase tracking-wider text-primary">
              Proof you can inspect
            </span>
            <h2 className="mt-3 text-3xl font-bold text-foreground sm:text-4xl">
              Verification and startup program screenshots
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Trust should be visible. This page is structured so users can
              inspect screenshots, read the safety explanation, and check
              Zapify&apos;s legal pages before connecting.
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {proofScreenshots.map((proof) => (
              <article
                key={proof.title}
                className="group overflow-hidden rounded-xl border border-border/60 bg-card/50 transition-all hover:border-primary/40 hover:shadow-lg dark:bg-card/30 cursor-pointer"
                onClick={() => setModalImage(proof.src)}
              >
                <div className="aspect-[16/10] overflow-hidden bg-muted/30 relative">
                  <Image
                    src={proof.src}
                    alt={`${proof.title} screenshot - click to enlarge`}
                    width={900}
                    height={560}
                    className="h-full w-full object-contain p-4 transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/5 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-background/90 backdrop-blur-sm rounded-full px-3 py-1.5 text-xs font-medium shadow-lg">
                      Click to enlarge
                    </div>
                  </div>
                </div>
                <div className="border-t border-border/40 p-5">
                  <h3 className="text-lg font-bold text-foreground">
                    {proof.title}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {proof.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Short Answer Section - Clean Card Design */}
      <section className="w-full bg-background py-10 sm:py-12">
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-border/60 bg-card p-6 text-center sm:p-8">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
              <HugeiconsIcon
                icon={Shield01Icon}
                size={24}
                className="text-green-600 dark:text-green-500"
              />
            </div>
            <h2 className="text-xl font-bold text-foreground sm:text-2xl">
              Is Zapify safe for Instagram automation?
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
              Yes. Zapify uses Meta authorization for eligible Business and Creator accounts, 
              never requires your Instagram password, and avoids risky methods like scraping or 
              unauthorized login workarounds. The safest setup is official access, clear consent, 
              and respect for Meta&apos;s platform policies.
            </p>
          </div>
        </div>
      </section>

      {/* Safety Features Section */}
      <section className="w-full py-12 sm:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 max-w-2xl">
            <span className="text-[0.7rem] font-bold uppercase tracking-wider text-primary">
              Safety and compliance
            </span>
            <h2 className="mt-3 text-3xl font-bold text-foreground sm:text-4xl">
              What Zapify does to protect account access
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              The goal is simple: help you automate conversations without
              handing over control of your Instagram account to unsafe tools.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {safetyFeatures.map((feature) => (
              <article
                key={feature.title}
                className="rounded-xl border border-border/60 bg-card/50 p-6 backdrop-blur-sm transition-all hover:border-border dark:bg-card/30"
              >
                <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <HugeiconsIcon
                    icon={CheckmarkCircle01Icon}
                    size={22}
                    className="text-primary"
                  />
                </div>
                <h3 className="text-lg font-bold text-foreground">
                  {feature.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {feature.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* What It Is Section */}
      <section className="w-full border-y border-border/40 bg-muted/30 py-12 dark:bg-muted/10 sm:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <span className="text-[0.7rem] font-bold uppercase tracking-wider text-primary">
                What it is
              </span>
              <h2 className="mt-3 text-2xl font-bold text-foreground sm:text-3xl lg:text-4xl">
                Meta verified technology access, not a blue-check service
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                &quot;Meta Verified&quot; can mean different things online. On
                this page, it refers to Zapify&apos;s verified/approved app
                access for Instagram automation workflows. It does not mean
                Zapify sells Meta Verified subscriptions or guarantees blue
                badge verification for Instagram profiles.
              </p>
            </div>
            <div className="rounded-xl border border-border/60 bg-card/50 p-6 dark:bg-card/30">
              <h3 className="text-lg font-bold text-foreground">
                What Zapify is built for
              </h3>
              <ul className="mt-4 space-y-3">
                {[
                  'Instagram comment to DM automation for creators, brands, and agencies.',
                  'Faster replies to comments, story replies, and direct messages.',
                  'Lead capture workflows for links, offers, product questions, and email collection.',
                  'AI-assisted responses that help you keep replies closer to your brand voice.',
                  'Eligible Instagram Business and Creator accounts that can connect through Meta.',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Comparison Section */}
      <section className="w-full border-y border-border/40 bg-muted/30 py-12 dark:bg-muted/10 sm:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 max-w-2xl">
            <span className="text-[0.7rem] font-bold uppercase tracking-wider text-primary">
              Risk comparison
            </span>
            <h2 className="mt-3 text-3xl font-bold text-foreground sm:text-4xl">
              Zapify vs risky Instagram automation tools
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Before you connect any Instagram automation platform, check how it
              handles access, permissions, and sending limits.
            </p>
          </div>

          <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/30 dark:to-rose-950/30">
                    <th className="px-4 py-4 text-left text-sm font-bold text-foreground sm:px-6">
                      Safety question
                    </th>
                    <th className="px-4 py-4 text-left text-sm font-bold text-foreground sm:px-6">
                      Zapify approach
                    </th>
                    <th className="px-4 py-4 text-left text-sm font-bold text-foreground sm:px-6">
                      Risky automation pattern
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {riskComparison.map((row, i) => (
                    <tr
                      key={row.question}
                      className={cn(
                        'border-b border-border/60 last:border-0',
                        i % 2 === 1 && 'bg-muted/20'
                      )}
                    >
                      <td className="px-4 py-5 text-sm font-medium text-foreground sm:px-6">
                        {row.question}
                      </td>
                      <td className="px-4 py-5 text-sm text-green-700 dark:text-green-400 sm:px-6">
                        {row.zapify.split('\n').map((line, idx) => (
                          <span key={idx} className={cn(idx === 0 && 'font-bold block')}>
                            {idx === 0 ? line : <span className="text-muted-foreground">{line}</span>}
                          </span>
                        ))}
                      </td>
                      <td className="px-4 py-5 text-sm text-red-700 dark:text-red-400 sm:px-6">
                        {row.risky.split('\n').map((line, idx) => (
                          <span key={idx} className={cn(idx === 0 && 'font-bold block')}>
                            {idx === 0 ? line : <span className="text-muted-foreground">{line}</span>}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Safe Setup Steps Section - Creative Timeline */}
      <section className="w-full py-12 sm:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 max-w-2xl">
            <span className="text-[0.7rem] font-bold uppercase tracking-wider text-primary">
              Safe setup flow
            </span>
            <h2 className="mt-3 text-3xl font-bold text-foreground sm:text-4xl">
              How to connect Zapify safely
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Keep this checklist open the first time you connect your
              Instagram account.
            </p>
          </div>

          {/* Connected Timeline Steps */}
          <div className="relative">
            {/* Connection Line */}
            <div className="absolute left-5 top-8 bottom-8 w-0.5 bg-gradient-to-b from-primary/40 via-primary/20 to-primary/40 hidden sm:block" />
            
            <div className="space-y-6">
              {safeSetupSteps.map((step, index) => (
                <article
                  key={step.title}
                  className="relative flex gap-4 sm:gap-6"
                >
                  {/* Step Number with Pulse Effect */}
                  <div className="relative shrink-0">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground shadow-lg shadow-primary/25 z-10 relative">
                      {index + 1}
                    </div>
                    {/* Pulse ring for active feel */}
                    <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" style={{ animationDuration: '3s', animationDelay: `${index * 0.5}s` }} />
                  </div>
                  
                  {/* Content Card */}
                  <div className="flex-1 rounded-xl border border-border/60 bg-card/50 p-4 transition-all hover:border-primary/30 hover:shadow-md hover:shadow-primary/5 dark:bg-card/30 sm:p-5">
                    <h3 className="text-base font-bold text-foreground sm:text-lg">
                      {step.title}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Checklist Section */}
      <section className="w-full border-y border-border/40 bg-muted/30 py-12 dark:bg-muted/10 sm:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <span className="text-[0.7rem] font-bold uppercase tracking-wider text-primary">
                Trust checklist
              </span>
              <h2 className="mt-3 text-2xl font-bold text-foreground sm:text-3xl">
                What users should verify before trusting any DM automation
                platform
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                This checklist helps users make a safer choice even if they
                compare Zapify with other Meta verified or Meta Business Partner
                automation tools.
              </p>
            </div>
            <div className="grid gap-5">
              <div className="rounded-xl border border-border/60 bg-card/50 p-5 dark:bg-card/30">
                <h3 className="text-lg font-bold text-green-700 dark:text-green-400">
                  Good signs
                </h3>
                <ul className="mt-3 space-y-2">
                  {goodSigns.map((sign) => (
                    <li key={sign} className="flex items-start gap-3">
                      <HugeiconsIcon
                        icon={CheckmarkCircle01Icon}
                        size={16}
                        className="mt-0.5 shrink-0 text-green-500"
                      />
                      <span className="text-sm text-muted-foreground">
                        {sign}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-border/60 bg-card/50 p-5 dark:bg-card/30">
                <h3 className="text-lg font-bold text-red-700 dark:text-red-400">
                  Warning signs
                </h3>
                <ul className="mt-3 space-y-2">
                  {warningSigns.map((sign) => (
                    <li key={sign} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-100 text-xs font-bold text-red-600 dark:bg-red-900 dark:text-red-400">
                        !
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {sign}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Matching Landing Page Style */}
      <section className="relative w-full overflow-hidden bg-transparent px-2.5 py-14 sm:px-6 sm:py-20 lg:px-8 lg:py-28" id="faq">
        <div className="pointer-events-none absolute -bottom-40 left-1/2 h-80 w-[82%] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(251,113,133,0.50)_0%,rgba(244,114,182,0.46)_18%,rgba(168,85,247,0.42)_36%,rgba(59,130,246,0.38)_56%,rgba(34,211,238,0.30)_72%,transparent_84%)] blur-3xl dark:opacity-45" />
        <div className="relative mx-auto w-full max-w-7xl rounded-2xl border border-border/50 bg-card/45 p-3.5 backdrop-blur-[2px] sm:p-6 dark:bg-card/20">
          <div className="grid gap-4 sm:gap-5 lg:grid-cols-[1.02fr_1.25fr]">
            {/* Left Panel */}
            <div className="rounded-2xl bg-transparent p-3.5 sm:p-7">
              <span className={cn(
                "inline-flex rounded-full bg-primary-gradient px-3 py-1 text-xs font-semibold text-white",
                themeColor === "yellow" && "!text-black"
              )}>FAQ</span>
              <h2 className="mt-3 text-[2rem] font-bold tracking-tight text-foreground sm:mt-4 sm:text-5xl">
                <span className="gradient-text">Got questions?</span>
                <br />
                We&apos;ve got answers.
              </h2>
              <p className="mt-3 max-w-md text-[13px] leading-relaxed text-muted-foreground sm:mt-4 sm:text-base">
                Direct answers about Zapify, Meta verification, and account safety for creators, ecommerce teams, and agencies.
              </p>
              <div className="mt-5 overflow-hidden rounded-2xl bg-transparent p-1.5 sm:mt-7 sm:p-2">
                <div className="relative aspect-[16/11] overflow-hidden rounded-xl">
                  {verifiedFaqSlides.map((slide, i) => (
                    <div
                      key={slide.light}
                      className={`absolute inset-0 transition-opacity duration-500 ${faqSlideIndex === i ? 'opacity-100' : 'opacity-0'}`}
                    >
                      <Image
                        src={slide.light}
                        alt={`Zapify interface - slide ${i + 1}`}
                        width={1200}
                        height={900}
                        className="absolute inset-0 h-full w-full object-cover dark:hidden"
                      />
                      <Image
                        src={slide.dark}
                        alt={`Zapify interface dark mode - slide ${i + 1}`}
                        width={1200}
                        height={900}
                        className="absolute inset-0 hidden h-full w-full object-cover dark:block"
                        aria-hidden
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex items-center justify-center gap-1.5">
                  {verifiedFaqSlides.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      aria-label={`Go to slide ${i + 1}`}
                      onClick={() => setFaqSlideIndex(i)}
                      className={`h-1.5 rounded-full transition-all ${faqSlideIndex === i ? 'w-5 bg-primary' : 'w-1.5 bg-border/90 dark:bg-zinc-600'}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Right: FAQ Accordion */}
            <div className="space-y-3">
              {faqData.map((faq, index) => {
                const isOpen = faqOpenIndex === index;
                return (
                  <div
                    key={faq.question}
                    onMouseEnter={() => setFaqOpenIndex(index)}
                    className="rounded-2xl border border-border/50 bg-card/90 px-4 py-3.5 transition-colors sm:px-6 sm:py-4 dark:bg-card/80"
                  >
                    <button
                      type="button"
                      onClick={() => setFaqOpenIndex((prev) => (prev === index ? -1 : index))}
                      className="flex w-full items-center justify-between gap-3 text-left"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-base leading-none">{faq.icon}</span>
                        <p className="text-base font-semibold leading-snug text-foreground sm:text-[1.15rem]">{faq.question}</p>
                      </div>
                      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground">
                        {isOpen ? (
                          <HugeiconsIcon icon={MinusSignIcon} size={16} color="currentColor" aria-hidden />
                        ) : (
                          <HugeiconsIcon icon={PlusSignIcon} size={16} color="currentColor" aria-hidden />
                        )}
                      </span>
                    </button>
                    <div
                      className={`grid transition-all duration-300 ${isOpen ? 'mt-2.5 grid-rows-[1fr] opacity-100 sm:mt-3' : 'grid-rows-[0fr] opacity-0'}`}
                    >
                      <p className="overflow-hidden pr-3 text-[13px] leading-relaxed text-muted-foreground sm:pr-8 sm:text-[0.95rem]">
                        {faq.answer.includes('Privacy Policy') ? (
                          <>
                            Review the current legal pages before connecting: the{' '}
                            <Link to="/privacy" className="font-medium text-primary hover:underline">
                              Zapify Privacy Policy
                            </Link>
                            ,{' '}
                            <Link to="/terms" className="font-medium text-primary hover:underline">
                              Terms of Service
                            </Link>
                            , and{' '}
                            <Link to="/contact" className="font-medium text-primary hover:underline">
                              Contact page
                            </Link>
                            .
                          </>
                        ) : (
                          faq.answer
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Support Box */}
          <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-border/50 bg-card/35 px-3.5 py-3.5 backdrop-blur-md sm:mt-5 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-4 dark:bg-card/25">
            <div className="flex items-center gap-3">
              <span className={cn(
                "inline-flex size-10 items-center justify-center rounded-xl bg-primary-gradient text-white",
                themeColor === "yellow" && "!text-black"
              )}>?</span>
              <div>
                <p className="text-[15px] font-semibold text-foreground sm:text-base">Still have questions?</p>
                <p className="text-[13px] text-muted-foreground sm:text-sm">Our team is online 24/7 and ready to help.</p>
              </div>
            </div>
            <a
              href="/contact"
              className={cn(
                "inline-flex h-10 items-center justify-center rounded-full bg-primary-gradient px-5 text-sm font-bold text-white dark:text-black sm:h-11 sm:px-6",
                "w-fit mx-auto sm:mx-0",
                themeColor === "yellow" && "!text-black"
              )}
            >
              Contact Support
            </a>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="w-full border-t border-border/40 bg-muted/30 py-8">
        <div className="mx-auto w-full max-w-7xl px-4 text-center text-xs text-muted-foreground sm:px-6 lg:px-8">
          Last updated: May 26, 2026. Instagram, Facebook, and Meta are
          trademarks of Meta Platforms, Inc. Zapify is an independent product.
          Meta controls platform permissions, limits, and policies.
        </div>
      </section>

      {/* Standard CTA */}
      <CTA />

      {/* Image Modal */}
      {modalImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setModalImage(null)}
        >
          <div className="relative max-w-6xl max-h-[90vh] w-full flex items-center justify-center">
            <button
              onClick={() => setModalImage(null)}
              className="absolute -top-12 right-0 text-white/80 hover:text-white text-sm font-medium flex items-center gap-1.5 transition-colors"
            >
              <span>Close</span>
              <span className="text-lg">&times;</span>
            </button>
            <img
              src={modalImage}
              alt="Enlarged view"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* AI Summarize Widget */}
      <AskAIExplain
        context="security"
        meta={{ pageTitle: "Zapify Meta Verified Tech Provider", pageUrl: "https://zapify.pro/verified" }}
        isWidget
      />
    </main>
  )
}
